// HOME GRID METRICS — the ONE geometry table of the workspace "/" board
// (tickets/reference/white-label-redesign/HOME_WIDGETS_SPEC.md §1).
//
// Every number the board's layout depends on lives here: the column steps,
// the gutters, the square-cell cap, the four widget sizes and what each size
// spans at each column count. pages/main.js and the home components read these
// functions; __tests__/homeGridMetrics.test.js pins every value to the spec
// table, so there is no second copy to drift.
//
// COLUMNS FOLLOW THE GRID'S OWN WIDTH, never the viewport — the assistant dock
// narrowing the page narrows the grid too. The board states this as container
// queries in its injected stylesheet (pages/main.js `_injectWidgetStyles`,
// generated from this table): the smbls atomic CSS engine does not wrap an
// `@container` key yet (it composes `.cls @container …`, dead CSS). Pure CSS
// also lays the board out in a hidden background tab, where rAF and
// ResizeObserver never fire. `homeGridMetrics` answers the same question for
// JS and for the lock test.
//
// NO APP TILES: app shortcuts never sit in the grid — they live in the ONE App
// strip between the greeting and the grid (decision H1 A, Nika "go all
// recommended", 2026-09-27; components/HomeAppStrip.js). So every step's grid
// is its cells, one track per cell (the phone step's half-tracks existed only
// to seat app tiles two up, and left with them).
//
// The px values are the spec's own geometry, stated once here on purpose (the
// spec: "one constants module … a lock test pins its px values to this
// table"). They are not spacing tokens: the spacing scale is em-based and the
// cell grid must not shrink with a card's font size.
//
// WIDER BOARD (Nika 2026-09-27: "also make main width more wider"): the cell
// cap is 300 and two wide steps join the table — 5 columns from a 1500 px
// grid, 6 from 1800. Each new step starts where its cell is about 280 px, the
// same rule the 4-column step follows (1180 -> 277): a column is added only
// when the cells stay at least as large as the old cap. The widest board is
// 6 x 300 + 5 x 24 = 1920 px (was 4 x 280 + 3 x 24 = 1192). The grid is the
// page column minus its two `C` gutters, so the viewports read roughly
// 1440 -> 4, 1680 -> 5, 1920 -> 6 columns.
export const HOME_GRID_CELL_CAP = 300

// THE PHONE STEPS (Nika 2026-09-28): on two columns and on one the cells FILL
// the column (`fill` — no cap: a 390 px phone no longer shows a 300 px card
// with bare stripes beside it), and one column drops the square-row rule
// (`rows: 'auto'` — each card is as tall as its content, never a 358 px
// square around three lines).
//
// TWO COLUMNS TOO (DESIGN-SHELL-RESPONSIVE-IOS-SAFARI-1, the Chains / Tokens
// lane's shell check): the two-column step is an iPad in portrait (744 /
// 834 -> a ~700-760 px grid) and a phone on its side, and there a filled
// cell is ~374 px, so an L (2 x 2) was a 760 x 761 square with its bottom
// half empty (chains-overview, tok-value, tok-activity on dev DFI). The rows
// follow the content there as on one column — the L reads as tall as on a
// phone, only wider.
export const HOME_GRID_STEPS = [
  { minWidth: 1800, cols: 6, gutter: 24 },
  { minWidth: 1500, cols: 5, gutter: 24 },
  { minWidth: 1180, cols: 4, gutter: 24 },
  { minWidth: 860, cols: 3, gutter: 16 },
  { minWidth: 520, cols: 2, gutter: 12, fill: true, rows: 'auto' },
  { minWidth: 0, cols: 1, gutter: 12, fill: true, rows: 'auto' }
]

export const HOME_WIDGET_SIZES = ['s', 'm', 'l', 'xl']

// { cols, gutter, cell, width, square } for a grid that is `width` px wide.
// `cell` is the cell side, floored to a whole px so every track lands on the
// pixel grid — capped, except on a `fill` step; `width` is what the cells +
// gutters actually use (the grid centres itself in whatever is left);
// `square` = rows are at least one cell tall (false on the one-column step,
// where a row is as tall as its card's content).
export const homeGridMetrics = (width) => {
  const w = Number(width) > 0 ? Number(width) : 0
  const step =
    HOME_GRID_STEPS.find((s) => w >= s.minWidth) || HOME_GRID_STEPS[HOME_GRID_STEPS.length - 1]
  const raw = (w - (step.cols - 1) * step.gutter) / step.cols
  const cell = Math.max(
    0,
    step.fill ? Math.floor(raw) : Math.min(HOME_GRID_CELL_CAP, Math.floor(raw))
  )
  return {
    cols: step.cols,
    gutter: step.gutter,
    cell,
    width: step.cols * cell + (step.cols - 1) * step.gutter,
    square: step.rows !== 'auto'
  }
}

// PER-STEP SPAN OVERRIDE (WS-HOME-GRID-SPAN-PER-STEP-1). The four sizes span
// the same cells at every step, and no set of them is whole on 6, 5 AND 4
// columns: a set whole on 6 and 4 has a cell total that is a multiple of 12,
// on 5 it must be a multiple of 5, LCM 60 (the VaultOS bento left three bare
// cells at 5, 2026-10-09). So a module's widget manifest row may name a
// different span for a column count:
//   spanAt: { <cols>: { c: <columns>, r: <rows> } }   e.g. { 5: { c: 3, r: 2 } }
// at its top level or in its `spec` (like `size`). It applies only while the
// card renders at its default size — a member who picked another size keeps
// that size's own span (functions/homeWidgetFrame.js `homeWidgetSpanVars`).
//
// STRICT: `c` must be an integer >= 1 (typeof number — never '3', true or
// [3]); `r` may be left out (one row), and when present it must be an integer
// >= 1 too — null, '', false or 2.5 make the whole entry no override. The
// columns clamp to the step, the rows to HOME_WIDGET_SPAN_MAX_ROWS. Returns
// { c, r } or null.
export const HOME_WIDGET_SPAN_MAX_ROWS = 4

const _whole = (v) => typeof v === 'number' && Number.isInteger(v) && v >= 1

export const homeWidgetSpanAt = (spanAt, cols) => {
  if (!spanAt || typeof spanAt !== 'object' || Array.isArray(spanAt)) return null
  const n = Math.floor(Number(cols))
  if (!(n >= 1)) return null
  if (!Object.prototype.hasOwnProperty.call(spanAt, n)) return null
  const o = spanAt[n]
  if (!o || typeof o !== 'object' || Array.isArray(o)) return null
  if (!_whole(o.c)) return null
  const hasR = Object.prototype.hasOwnProperty.call(o, 'r')
  if (hasR && !_whole(o.r)) return null
  const r = hasR ? o.r : 1
  return { c: Math.min(o.c, n), r: Math.min(r, HOME_WIDGET_SPAN_MAX_ROWS) }
}

// What a size spans at a column count: S 1×1 · M 2×1 · L 2×2 · XL the full
// row (4 wide on 4 columns, 5 on 5, 6 on 6), clamped to the columns there
// are. On one column every size is one wide and L keeps its two rows (spec §1
// "Sizes"). An unknown size reads as S. A usable `spanAt` override for this
// column count (homeWidgetSpanAt) wins; the caller passes it only for a card
// at its default size.
export const homeWidgetSpan = (size, cols, spanAt) => {
  const n = Math.max(1, Math.floor(Number(cols)) || 1)
  const own = homeWidgetSpanAt(spanAt, n)
  if (own) return own
  const spans = { s: [1, 1], m: [2, 1], l: [2, 2], xl: [n, 1] }
  const [c, r] = spans[size] || spans.s
  if (n === 1) return { c: 1, r: size === 'l' ? 2 : 1 }
  return { c: Math.min(c, n), r }
}

// The slot grid's arrangement per size (spec §2 "Per size"). The element
// layer declares the same four blocks on components/HomeWidgetBody.js (a
// component cannot import); __tests__/homeSlotBodyArrangement.test.js pins
// the two copies equal. The board stylesheet reads THIS table.
export const HOME_BODY_LAYOUTS = {
  s: {
    columns: 'minmax(0, 1fr)',
    areas: '"hero" "delta" "visual" "footer"',
    rows: 'auto auto auto minmax(0, 1fr)'
  },
  m: {
    columns: 'minmax(0, 1fr) minmax(0, 1fr)',
    areas: '"hero visual" "delta visual" "footer footer"',
    rows: 'auto minmax(0, 1fr) auto'
  },
  l: {
    columns: 'minmax(0, 1fr)',
    areas: '"hero" "delta" "visual" "list" "footer"',
    rows: 'auto auto auto minmax(0, 1fr) auto'
  },
  xl: {
    columns: 'minmax(0, 1fr) minmax(0, 1.25fr) minmax(0, 1.25fr)',
    areas: '"hero visual list" "delta visual list" "footer footer footer"',
    rows: 'auto minmax(0, 1fr) auto'
  }
}

// The body arrangement a size gets at a column count — the one of the width
// it really spans. A size spanning two rows is the one-column L; one cell
// wide is S; two cells wide is M; three or more is XL. So on one column M and
// XL lay out as S (one cell wide), and on two columns XL lays out as M; every
// other size keeps its own arrangement.
export const homeBodyFit = (size, cols, spanAt) => {
  const { c, r } = homeWidgetSpan(size, cols, spanAt)
  if (r > 1) return 'l'
  if (c <= 1) return 's'
  if (c === 2) return 'm'
  return 'xl'
}

// THE OVERRIDE RULES — static, from the table, written once into the board
// sheet (pages/main.js `_injectWidgetStyles`). A card carries its own
// override (components/HomeWidgetFrame.js, functions/homeWidgetFrame.js):
//   • its span as custom properties `--home-span-c-<cols>` /
//     `--home-span-r-<cols>`, which the board's size rule reads with the size's
//     own span as the fallback (homeSpanVar);
//   • when the override changes the width its body really spans, the step and
//     the arrangement in `data-span-fit` ("5:m" — one `<cols>:<fit>` word per
//     step), which the rules below turn into that arrangement.
// Nothing here knows a widget id, so no sheet has to follow the rows.
// The span value the size rule at `cols` columns writes: the card's override
// variable, else `fallback` (the size's own span).
export const homeSpanVar = (axis, cols, fallback) =>
  'var(--home-span-' + axis + '-' + cols + ', ' + fallback + ')'

const _hasList = (fit) =>
  /(^|[\s"])list([\s"]|$)/.test((HOME_BODY_LAYOUTS[fit] || HOME_BODY_LAYOUTS.s).areas)

// The `data-span-fit` rules, one bounded container-query block per step. Each
// rule outranks the board's per-size fit rule (same weight, later in the sheet)
// and the slot components' own size blocks (more weight). A fit without a list
// hides the list (a list that is the body's only visual keeps the visual
// area); a fit with one shows it, as the size's own component block does
// (components/HomeWidgetList.js).
export const homeSpanFitCss = () => {
  const steps = HOME_GRID_STEPS.slice().sort((a, b) => a.minWidth - b.minWidth)
  return steps
    .map((step, i) => {
      const next = steps[i + 1]
      const rules = []
      for (const fit of HOME_WIDGET_SIZES) {
        const at = ".home-grid > [data-span-fit~='" + step.cols + ':' + fit + "']"
        const to = HOME_BODY_LAYOUTS[fit]
        rules.push(
          at +
            ' .home-widget-body { grid-template-columns: ' +
            to.columns +
            '; grid-template-areas: ' +
            to.areas +
            '; grid-template-rows: ' +
            to.rows +
            '; }'
        )
        if (_hasList(fit)) {
          rules.push(at + ' .home-widget-list:not([data-as-visual]) { display: flex; }')
          rules.push(
            at +
              ' .home-widget-list[data-as-visual] { display: flex; ' +
              (fit === 'xl'
                ? 'grid-row: visual; grid-column: visual-start / list-end; }'
                : 'grid-column: visual; grid-row: visual-start / list-end; }')
          )
        } else {
          rules.push(at + ' .home-widget-list:not([data-as-visual]) { display: none; }')
          rules.push(
            at + ' .home-widget-list[data-as-visual] { grid-column: visual; grid-row: visual; }'
          )
        }
      }
      let range = ''
      if (step.minWidth > 0 && next)
        range = '(' + step.minWidth + 'px <= width < ' + next.minWidth + 'px)'
      else if (step.minWidth > 0) range = '(width >= ' + step.minWidth + 'px)'
      else if (next) range = '(width < ' + next.minWidth + 'px)'
      return range
        ? '@container homeboard ' + range + ' {\n' + rules.join('\n') + '\n}'
        : rules.join('\n')
    })
    .join('\n')
}

// The card's span variables for a `spanAt`: `--home-span-c-<cols>` and
// `--home-span-r-<cols>` for every step it names (the size rule's homeSpanVar
// reads them). {} when it names none.
export const homeSpanVarsFor = (spanAt) => {
  const out = {}
  for (const step of HOME_GRID_STEPS) {
    const span = homeWidgetSpanAt(spanAt, step.cols)
    if (!span) continue
    out['--home-span-c-' + step.cols] = String(span.c)
    out['--home-span-r-' + step.cols] = String(span.r)
  }
  return out
}

// The `data-span-fit` words of a card: for each step where its override
// changes the width its body really spans, `<cols>:<fit>`. `size` is the size
// the card renders at (the caller passes the override only at the default
// size). '' when nothing changes.
export const homeSpanFitWords = (size, spanAt) =>
  HOME_GRID_STEPS.map((step) => {
    if (!homeWidgetSpanAt(spanAt, step.cols)) return ''
    const fit = homeBodyFit(size, step.cols, spanAt)
    return fit === homeBodyFit(size, step.cols) ? '' : step.cols + ':' + fit
  })
    .filter(Boolean)
    .join(' ')

// The size a GridStack-era cell reads as — 12 columns, `gs-w` × `gs-h`. Two
// callers: the one-time v2 layout migration snaps a saved 12-column entry to
// the nearest allowed size, and a module manifest's legacy `spec` (w × h)
// gives its widget a default size.
//
// A new-grid cell is about three GridStack columns wide, so: eight or more
// columns is a full-width band (XL); a wide card is M; a big or tall card is
// the vertical L (hero, visual, list), which fills the height the tall 3×4
// module default already has instead of leaving a void under an S; anything
// else is S. GridStack drops `gs-w` / `gs-h` for a span of one, so missing
// values read as 1.
export const homeWidgetSizeForCell = (w, h) => {
  const cw = Number(w) > 0 ? Number(w) : 1
  const ch = Number(h) > 0 ? Number(h) : 1
  if (cw >= 8) return 'xl'
  if (cw >= 5 && cw >= ch * 1.4) return 'm'
  if (ch >= 4 && (cw >= 4 || ch > cw)) return 'l'
  return 's'
}
